<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { useToast } from '@/composables/toast'
import { errToStr } from '@/helpers/tools'
import { Liegenschaft } from '@/models/Liegenschaft'
import { onMounted, ref, watch, computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { registerFormRouteGuard } from '@/composables/routerGuard'
import LiegenschaftFormFields from '@/components/liegenschaft/form-fields.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { createModel } from '@kp/js-model'

const { showInDialog = false } = defineProps<{
    showInDialog?: boolean
}>()

const loading = ref(false)
const initialLoading = ref(false)
const confirmDelDlg = ref(false)
const liegenschaft: Ref<Liegenschaft | null> = ref(null)
const valid = ref(false)

const route = useRoute()
const isNew = computed(() => {
    return route.params.id === 'new'
})

registerFormRouteGuard(isDirty)

const toast = useToast()
const { t } = useI18n()
const router = useRouter()

watch(route, (value) => {
    if (liegenschaft.value && liegenschaft.value?.id === parseInt(value.params.id as string, 10)) {
        return
    }
    loadRecord(route.params.id as string)
})

onMounted(() => {
    loadRecord(route.params.id as string)
})

defineExpose({ isDirty })

async function loadRecord(id: string | number) {
    if (id === 'new') {
        liegenschaft.value = createModel(Liegenschaft)
        initialLoading.value = false
        return
    }

    try {
        initialLoading.value = true
        id = parseInt(id as string, 10)
        liegenschaft.value = await Liegenschaft.loadById(id || -1)
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        initialLoading.value = false
    }
}
async function onSubmit() {
    try {
        await liegenschaft.value?.save()
        closeForm()
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    }
}

async function onDestroy() {
    confirmDelDlg.value = true
}
function closeForm() {
    if (window.history.state.back === window.history.state.current) {
        router.push('liegenschaften')
    } else {
        router.go(-1)
    }
}

async function confirmDestroy() {
    try {
        await liegenschaft.value?.destroy()
        liegenschaft.value = null
        closeForm()
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

function isDirty() {
    return liegenschaft.value ? liegenschaft.value.isDirty() : false
}
</script>

<template>
    <v-skeleton-loader
        v-if="initialLoading"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <KpToolbar icon="fas fa-building" :title="t('liegenschaft')"> </KpToolbar>
        <v-container fluid>
            <v-card :class="{ 'centered-card': !showInDialog }">
                <v-form v-model="valid" autocomplete="new-password">
                    <v-card-text>
                        <LiegenschaftFormFields
                            :liegenschaft="liegenschaft"
                            :loading="loading"
                        ></LiegenschaftFormFields>
                        <span style="padding: 5px">{{ t('pflichtfelder_markiert') }}</span>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            v-if="!isNew"
                            class="hidden-sm-and-up"
                            color="red"
                            :disabled="loading"
                            text
                            @click="onDestroy"
                        >
                            <v-icon>fas fa-trash</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="!isNew"
                            class="hidden-xs"
                            color="red"
                            :disabled="loading"
                            text
                            @click="onDestroy"
                        >
                            {{ t('delete') }}
                        </v-btn>

                        <v-spacer />
                        <v-btn
                            class="hidden-sm-and-up"
                            color="decline"
                            text
                            :disabled="loading"
                            @click="closeForm"
                        >
                            <v-icon>fas fa-arrow-left</v-icon>
                        </v-btn>
                        <v-btn
                            class="hidden-xs"
                            color="decline"
                            :disabled="loading"
                            text
                            @click="closeForm"
                        >
                            {{ t('close') }}
                        </v-btn>
                        <v-btn
                            class="hidden-xs"
                            color="action white--text"
                            variant="flat"
                            :disabled="!valid || loading"
                            :loading="loading"
                            @click="onSubmit"
                            prepend-icon="fa-solid fa-floppy-disk"
                        >
                            {{ t('save') }}
                        </v-btn>
                        <v-btn
                            class="hidden-sm-and-up"
                            color="action white--text"
                            variant="flat"
                            :disabled="!valid || loading"
                            :loading="loading"
                            @click="onSubmit"
                            icon="fa-solid fa-floppy-disk"
                        >
                        </v-btn>
                    </v-card-actions>
                    <ConfirmDlg
                        v-model:model-value="confirmDelDlg"
                        :title="t('bestaetigung')"
                        :text="t('really_delete_rec')"
                        @yes="confirmDestroy"
                    />
                </v-form>
            </v-card>
        </v-container>
    </div>
</template>
